import { isEmpty, mergeRight, path, pathOr, pipe } from 'ramda';
import { useStore, useStoreMap } from 'effector-react';
import { JSONSchema } from '@invent/json-settings';
import { DASHBOARD_HEADER_WIDGET_ID } from '@/src/constants';
import { $storeWidgets, $platformWidgets, $allWidgets, $portalSettings } from './store';
import type {
  IWidget,
  IWidgetMetadata,
  DashboardWidgetsStoreT,
  CommonWidgetStoreT,
  PortalWidgetsSettingsT,
  IPlatformWidget,
  IPlatformWidgetsStore,
  IPortalSettingsStore,
  NavGraphT
} from './types';

const emptyObject = {};
const emptyJSONSettingsObject: JSONSchema = { jsonSchema: {}, uiSchema: {}, extraErrors: {} };

export const useStoreWidgetsSelector = () => useStore($storeWidgets);
export const usePortalSettingsSelector = () => useStore($portalSettings);

export function widgetsStoreListSelector(
  widgets: DashboardWidgetsStoreT
): IWidgetMetadata<Record<string, unknown>>[] {
  return Object.values(widgets);
}

function allWidgetsStoreListSelector(widgets: CommonWidgetStoreT): IWidget[] {
  return Object.values(widgets);
}

export const useAllWidgetsStoreListSelector = () =>
  useStoreMap($allWidgets, allWidgetsStoreListSelector);

export const useWidgetsStoreListSelector = () =>
  useStoreMap($storeWidgets, widgetsStoreListSelector);

export function defaultPropsWidgetSelector(
  store: DashboardWidgetsStoreT,
  widgetType: string
): DefProp<Record<string, unknown>> | Record<string, unknown> {
  return pipe(
    widgetsStoreListSelector,
    (widgets) => widgets.find(({ name }) => name === widgetType)?.defaultProps ?? emptyObject
  )(store);
}

function widgetDetailsWidgetSelector(
  store: DashboardWidgetsStoreT,
  widgetType: string
): DefProp<Record<string, unknown>> | Record<string, unknown> {
  return pipe(
    widgetsStoreListSelector,
    (widgets) => widgets.find(({ name }) => name === widgetType)?.widgetDetails ?? emptyObject
  )(store);
}

export function settingsSchemaWidgetSelector(
  store: CommonWidgetStoreT,
  widgetType: string
): JSONSchema {
  return pipe(allWidgetsStoreListSelector, (widgets) => {
    return (
      widgets.find(({ name }) => name === widgetType)?.settingsSchema ?? emptyJSONSettingsObject
    );
  })(store);
}

export const useWidgetDetailsWidgetSelector = (widgetType: string) =>
  useStoreMap($storeWidgets, (widgets) => widgetDetailsWidgetSelector(widgets, widgetType));

export const useSettingsSchemaWidgetSelector = (widgetType: string) =>
  useStoreMap({
    store: $allWidgets,
    keys: [widgetType],
    fn: (widgets, [widgetType]) => settingsSchemaWidgetSelector(widgets, widgetType)
  });

function widgetIdByPlacementSelector(
  store: IPlatformWidgetsStore,
  { domain, placement }: { domain: string; placement: string }
): number | undefined {
  return path(['byDomain', domain, placement], store);
}

function platformWidgetSelector(
  store: IPlatformWidgetsStore,
  { domain, placement }: { domain: string; placement: string }
): IPlatformWidget | undefined {
  const widgetId = widgetIdByPlacementSelector(store, { domain, placement });
  return path<IPlatformWidget | undefined>(['byId', widgetId || 'unknownId'], store);
}

export const usePlatformWidgetSelector = (domain: string, placement: string) => {
  return useStoreMap($platformWidgets, (store) =>
    platformWidgetSelector(store, { domain, placement })
  );
};

function platformWidgetsMetaByIdSelector(
  platformWidgets: IPlatformWidgetsStore
): Record<string, IPlatformWidget> {
  return platformWidgets.byId || emptyObject;
}

export const usePlatformWidgetsMetaByIdSelector = () =>
  useStoreMap($platformWidgets, platformWidgetsMetaByIdSelector);

function platformWidgetMetaByIdSelector(
  store: IPlatformWidgetsStore,
  widgetId: string
): IPlatformWidget | undefined {
  return path(['byId', widgetId], store);
}

export const usePlatformWidgetMetaByIdSelector = (widgetId: string) =>
  useStoreMap({
    store: $platformWidgets,
    keys: [widgetId],
    fn: (store, [widgetId]) => platformWidgetMetaByIdSelector(store, widgetId)
  });

export const usePlatformWidgetsLoadedSelector = () =>
  useStoreMap($platformWidgets, (store) => store.loaded);

export const usePublicWidgetsLoadedSelector = () =>
  useStoreMap($platformWidgets, (store) => store.publicLoaded);

type PlatformSettingsKeysT = 'navgraph' | 'widgets';
export function settingsSelector<T extends PlatformSettingsKeysT>(
  settingKey: PlatformSettingsKeysT,
  store: IPortalSettingsStore
): T extends 'navgraph' ? NavGraphT : PortalWidgetsSettingsT {
  const presets = ['personal', 'default'];

  const availableSettings = presets.reduce((result, presetName) => {
    const settings = pathOr(emptyObject, [settingKey, presetName], store);
    if (isEmpty(settings)) return result;
    return settings;
  }, emptyObject);

  return availableSettings;
}

export const usePortalWidgetSettingsSelector = (widgetId: number | undefined, placement: string) =>
  useStoreMap({
    store: $portalSettings,
    keys: [widgetId, placement],
    fn: (store, [widgetId, placement]) => {
      // return navGraph for graph widget
      if (placement === 'graph') {
        return {
          // TODO: @deprecated
          navGraph: mergeRight(
            pathOr(emptyObject, ['navgraph', 'default'], store),
            pathOr(emptyObject, ['navgraph', 'personal'], store)
          )
        };
      }
      if (widgetId) {
        return settingsSelector<'widgets'>('widgets', store)?.[widgetId] || {};
      }
      return null;
    }
  });

export const useGetDashboardPageContextConfig = (
  dashboardId?: string | null
): { urlParamKey: string; type: string } | undefined => {
  return useStoreMap({
    store: $portalSettings,
    keys: [dashboardId],
    fn: path([
      'widgets',
      'default',
      DASHBOARD_HEADER_WIDGET_ID,
      'pageContextConfig',
      'dashboards',
      dashboardId ?? ''
    ])
  });
};

export const useGetDashboardEntityType = (dashboardId?: string | null): string | null => {
  return useGetDashboardPageContextConfig(dashboardId)?.urlParamKey ?? null;
};
